import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ActivityIndicator } from 'react-native';

import {
  IconNearMe,
  IconNearMeDisabled,
  Pressable,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';

import { useRequestLocationPermission } from '../../hooks/use-request-location-permission';
import { LocationServicesModal } from '../empty-state/location-services-modal';

interface LocateMeButtonProps {
  onRecenter: () => void;
}

/**
 * Recentres the map on the guest.
 *
 * The legacy `ResetLocationButton` does the same job, but it renders as a pill-shaped
 * `Button size="xl"` and is shared with the flag-off map, so restyling it to the circular
 * control in the v2 design would change the old map too. The permission branching is
 * repeated here instead, which is the smaller cost.
 */
export const LocateMeButton: React.VFC<LocateMeButtonProps> = ({ onRecenter }) => {
  const { formatMessage } = useIntl();
  const styles = useLocateMeButtonMqStyles();
  const iconSize = useMqSelect({ $gteDesktop: 32 }, 18);

  const { loadingUserCoordinates } = useGeolocation();

  // The map is on screen from the moment the locator opens and the empty state already
  // offers the location prompt, so this only asks when the guest actually taps it. Shared
  // with the empty state's "Enable location services" link — see the hook for why a tap
  // always re-runs the check instead of assuming a cached denial is final.
  const { isDeniedModalOpen, closeDeniedModal, requestLocation, isPermissionGranted, isPermissionDenied } =
    useRequestLocationPermission({ onGranted: onRecenter });

  const handlePress = useCallback(() => {
    if (isPermissionGranted) {
      onRecenter();
      return;
    }

    requestLocation();
  }, [isPermissionGranted, onRecenter, requestLocation]);

  return (
    <>
      <LocationServicesModal isOpen={isDeniedModalOpen} onClose={closeDeniedModal} />
      <Pressable
        testID="reset-location-button"
        accessibilityLabel={formatMessage({ id: 'resetLocationButtonAria' })}
        dd-action-name="store locator recenter map"
        onPress={handlePress}
        disabled={loadingUserCoordinates}
        borderRadius={999}
        style={styles.button}
        // The design draws this at 32pt on small screens, under the 44pt minimum touch target.
        hitSlop={12}
      >
        {loadingUserCoordinates ? (
          <ActivityIndicator size="small" color={tokens.colors.$houseDark} />
        ) : isPermissionDenied ? (
          <IconNearMeDisabled size={iconSize} color={tokens.colors.$houseDark} />
        ) : (
          <IconNearMe size={iconSize} color={tokens.colors.$houseDark} />
        )}
      </Pressable>
    </>
  );
};

const useLocateMeButtonMqStyles = createMqStyles({
  button: {
    $base: {
      alignItems: 'center',
      backgroundColor: tokens.colors.$white,
      borderRadius: 999,
      elevation: 4,
      height: 32,
      justifyContent: 'center',
      shadowColor: tokens.colors.$black,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 4,
      width: 32,
    },
    $gteDesktop: {
      height: 56,
      width: 56,
    },
  },
});
